import { Briefcase, Building2, FileText, Users, ChevronRight, ClipboardCheck, ClipboardList, Calendar, Shield, MapPin, Stamp } from 'lucide-react'
import { usePage } from '@inertiajs/react'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from '@/components/ui/sidebar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { AccountSwitcher } from './AccountSwitcher'
import { NavUser } from './NavUser'
import { DashboardPageProps } from '@/types'

type NavItem = {
  title: string
  url: string
  icon: React.ComponentType<{ className?: string }>
}

type NavGroup = {
  title: string
  icon: React.ComponentType<{ className?: string }>
  children: { title: string; url: string }[]
}

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { auth } = usePage<DashboardPageProps>().props
  const accountSlug = auth.account?.slug

  const mainNavItems: NavItem[] = [
    {
      title: 'Businesses',
      url: `/${accountSlug}/businesses`,
      icon: Briefcase,
    },
    {
      title: 'Formations',
      url: `/${accountSlug}/formations`,
      icon: Building2,
    },
    {
      title: 'Calendar',
      url: `/${accountSlug}/calendar`,
      icon: Calendar,
    },
  ]

  const complianceGroup: NavGroup = {
    title: 'Compliance',
    icon: ClipboardCheck,
    children: [
      { title: 'Compliance Filings', url: `/${accountSlug}/compliance_filings` },
      { title: 'Tax Registration', url: `/${accountSlug}/tax_registrations` },
    ],
  }

  const licensesItem: NavItem = {
    title: 'Licenses',
    url: `/${accountSlug}/licenses`,
    icon: FileText,
  }

  const nomineeGroup: NavGroup = {
    title: 'Nominees',
    icon: Users,
    children: [
      { title: 'Directors', url: `/${accountSlug}/nominee_directors` },
      { title: 'Shareholders', url: `/${accountSlug}/nominee_shareholders` },
    ],
  }

  const registeredGroup: NavGroup = {
    title: 'Registered Agent',
    icon: Shield,
    children: [
      { title: 'Registered Agent', url: `/${accountSlug}/registered_agents` },
      { title: 'Registered Address', url: `/${accountSlug}/registered_addresses` },
    ],
  }

  const notarizationItem: NavItem = {
    title: 'Notarizations',
    url: `/${accountSlug}/notarizations`,
    icon: Stamp,
  }

  const bottomNavItems: NavItem[] = [
    {
      title: 'Tasks',
      url: `/${accountSlug}/business_tasks`,
      icon: ClipboardList,
    },
  ]

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
  const complianceActive = complianceGroup.children.some(
    c => currentPath === c.url || currentPath.startsWith(`${c.url}/`)
  )
  const nomineeActive = nomineeGroup.children.some(
    c => currentPath === c.url || currentPath.startsWith(`${c.url}/`)
  )
  const registeredActive = registeredGroup.children.some(
    c => currentPath === c.url || currentPath.startsWith(`${c.url}/`)
  )

  function renderCollapsibleGroup(group: NavGroup, isActive: boolean) {
    return (
      <Collapsible asChild defaultOpen={isActive} className="group/collapsible">
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton tooltip={group.title} isActive={isActive}>
              <group.icon className="size-4" />
              <span>{group.title}</span>
              <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {group.children.map((child) => (
                <SidebarMenuSubItem key={child.title}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={currentPath === child.url || currentPath.startsWith(`${child.url}/`)}
                  >
                    <a href={child.url}>{child.title}</a>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    )
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <AccountSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={currentPath === item.url || currentPath.startsWith(`${item.url}/`)}
                    tooltip={item.title}
                  >
                    <a href={item.url}>
                      <item.icon className="size-4" />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {renderCollapsibleGroup(complianceGroup, complianceActive)}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={currentPath === licensesItem.url || currentPath.startsWith(`${licensesItem.url}/`)}
                  tooltip={licensesItem.title}
                >
                  <a href={licensesItem.url}>
                    <licensesItem.icon className="size-4" />
                    <span>{licensesItem.title}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {renderCollapsibleGroup(nomineeGroup, nomineeActive)}
              {renderCollapsibleGroup(registeredGroup, registeredActive)}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={currentPath === notarizationItem.url || currentPath.startsWith(`${notarizationItem.url}/`)}
                  tooltip={notarizationItem.title}
                >
                  <a href={notarizationItem.url}>
                    <notarizationItem.icon className="size-4" />
                    <span>{notarizationItem.title}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {bottomNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={currentPath === item.url || currentPath.startsWith(`${item.url}/`)}
                    tooltip={item.title}
                  >
                    <a href={item.url}>
                      <item.icon className="size-4" />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        {(auth.identity?.backoffice_admin || auth.identity?.backoffice_compliance || auth.identity?.backoffice_support || auth.identity?.notary) && (
          <SidebarMenu>
            {(auth.identity?.backoffice_admin || auth.identity?.backoffice_compliance || auth.identity?.backoffice_support) && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Backoffice">
                  <a href="/backoffice" className="text-muted-foreground">
                    <Building2 className="size-4" />
                    <span>Backoffice</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            {auth.identity?.notary && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Notary Portal">
                  <a href="/notary_portal" className="text-muted-foreground">
                    <Stamp className="size-4" />
                    <span>Notary Portal</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        )}
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
